/**
 * The seasons painted behind the price funnel, at five time frames (Jason, 2026-10-07: "anywhere the funnel banner art
 * is used there needs to be a 2 month, 6 month, 1, 2, and 3 year version, never clipped on the top, and adjustable so
 * that any time frame can be used and it still looks good. the start should be 2 month time frame"). Each painting
 * covers a span of the calendar from a fixed slot: two months from January, March, May, July, September or November,
 * six from January or July, and one, two or three years from a January. The funnel lays the paintings of one span end
 * to end along its dates, each the full breadth of the drawing (never clipped, whatever its length), and picks the span
 * whose paintings, laid so, come nearest the shape they were painted at: a young job's few weeks take the two-month
 * paintings, a long build the years. One rule for what is painted (the API) and what is drawn (the funnel).
 */

/** The spans, in months, shortest first: the first is where a job starts. */
export const SEASON_SPANS = [2, 6, 12, 24, 36] as const;
export type SeasonSpan = (typeof SEASON_SPANS)[number];

export const SEASON_SPAN_WORDS: Record<SeasonSpan, string> = { 2: 'Two months', 6: 'Six months', 12: 'A year', 24: 'Two years', 36: 'Three years' };

/** A painting's place in the calendar: the span it covers and the month it starts (January for a year or more). */
export interface SeasonSlot {
  span: SeasonSpan;
  month: number;
}

/** One painting laid on the funnel: its slot and the days it covers, `from` to `to` (the day after its last). */
export interface SeasonTile extends SeasonSlot {
  from: string;
  to: string;
}

type Direction = 'seasons_across' | 'seasons_down';

const DAY = 86_400_000;
const time = (d: string) => Date.parse(`${d}T12:00:00Z`);
const iso = (y: number, m0: number) => new Date(Date.UTC(y + Math.floor(m0 / 12), ((m0 % 12) + 12) % 12, 1, 12)).toISOString().slice(0, 10);

/** How many months a time frame runs. */
export function seasonMonths(from: string, to: string): number {
  return Math.max(0, (time(to) - time(from)) / (30.44 * DAY));
}

/**
 * Each direction's drawing (web `PriceFunnel`): how long time runs on screen against the breadth a painting is given,
 * for a time frame of so many months, and the shape the painting is made at, length over breadth. Across: 960 of the
 * drawing's units of time over the 250 under the funnel, the painting 3:1. Down a phone: the funnel's length (it grows
 * with the months) over the 176 beside it, the painting 2:1 along time.
 */
export const SEASON_DRAWING: Record<Direction, { length: (months: number) => number; breadth: number; art: number }> = {
  seasons_across: { length: () => 960, breadth: 250, art: 3 },
  seasons_down: { length: (months) => Math.max(420, Math.round(months * 24)) - 28, breadth: 176, art: 2 },
};

/** The span that suits a time frame drawn one way: its paintings, laid along their dates, near the shape they were made at. */
export function seasonSpanFor(from: string, to: string, direction: Direction): SeasonSpan {
  const months = Math.max(seasonMonths(from, to), 0.5);
  const d = SEASON_DRAWING[direction];
  // A painting of `span` months covers span/months of the drawing's length: it keeps its shape when that is `art` breadths long.
  const ideal = (months * d.art * d.breadth) / d.length(months);
  // The longest span whose strips come out at most a quarter longer than the shape they were painted at: a job's first
  // months take the two-month strips (the start), and each longer span once the frame has grown into it.
  return [...SEASON_SPANS].reverse().find((span) => span <= ideal * 1.25) ?? SEASON_SPANS[0];
}

/** The paintings of a span laid along a time frame, in order, from the one that holds its first day to the one that holds its last. */
export function seasonTiles(span: SeasonSpan, from: string, to: string): SeasonTile[] {
  const start = new Date(time(from));
  const end = time(to);
  const out: SeasonTile[] = [];
  if (span < 12) {
    // Slots from January: two months from the odd months, six from January and July.
    let m0 = start.getUTCMonth() - (start.getUTCMonth() % span);
    const y = start.getUTCFullYear();
    for (let i = 0; i < 40; i++, m0 += span) {
      const a = iso(y, m0);
      if (time(a) > end) break;
      out.push({ span, month: (((m0 % 12) + 12) % 12) + 1, from: a, to: iso(y, m0 + span) });
    }
    return out;
  }
  // A year or more from a January, laid from the year the time frame starts.
  const years = span / 12;
  for (let y = start.getUTCFullYear(), i = 0; i < 40; i++, y += years) {
    const a = iso(y, 0);
    if (time(a) > end) break;
    out.push({ span, month: 1, from: a, to: iso(y + years, 0) });
  }
  return out;
}

/** The months a painting covers, January first, in order: what it is asked to show. */
export function seasonSlotMonths(slot: SeasonSlot): number[] {
  return Array.from({ length: Math.min(slot.span, 12) }, (_, i) => ((slot.month - 1 + i) % 12) + 1);
}

/**
 * Which span to draw, from the paintings there are: the one that suits the time frame when its paintings are all there,
 * else the span nearest it whose paintings are, else the one with the most of them; the span that suits it is what is
 * painted next. `has` says whether a slot's painting is there.
 */
export function seasonSpanToDraw(from: string, to: string, direction: Direction, has: (slot: SeasonSlot) => boolean): SeasonSpan | null {
  const best = seasonSpanFor(from, to, direction);
  const near = [...SEASON_SPANS].sort((a, b) => Math.abs(Math.log(a / best)) - Math.abs(Math.log(b / best)) || a - b);
  const share = (s: SeasonSpan) => {
    const tiles = seasonTiles(s, from, to);
    return tiles.length ? tiles.filter(has).length / tiles.length : 0;
  };
  const whole = near.find((s) => share(s) === 1);
  if (whole) return whole;
  const some = near.filter((s) => share(s) > 0).sort((a, b) => share(b) - share(a))[0];
  return some ?? null;
}
